import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import PainPointsSection from './components/PainPointsSection';
import SolutionOverview from './components/SolutionOverview';
import ROICalculator from './components/ROICalculator';
import TestimonialsSection from './components/TestimonialsSection';
import PricingSection from './components/PricingSection';
import ImplementationSection from './components/ImplementationSection';
import FAQSection from './components/FAQSection';
import ContactSection from './components/ContactSection';

const LandingPage = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleDemoRequest = () => {
    const demoSection = document.getElementById('request-demo');
    if (demoSection) {
      const headerHeight = 80;
      const targetPosition = demoSection.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleROICalculator = () => {
    const roiSection = document.getElementById('roi-calculator');
    if (roiSection) {
      const headerHeight = 80;
      const targetPosition = roiSection.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        <HeroSection 
          onDemoRequest={handleDemoRequest}
          onROICalculator={handleROICalculator}
        />
        
        <PainPointsSection />
        
        <SolutionOverview />
        
        <ROICalculator />
        
        <TestimonialsSection />
        
        <PricingSection />
        
        <ImplementationSection />
        
        <FAQSection />
        
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="relative bg-white/70 backdrop-blur-xl border-t border-border py-16 mt-24 overflow-hidden">
        {/* Gradient Accent Line */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-400 via-primary-400 to-accent-600 opacity-80 animate-pulse" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="md:col-span-2 flex flex-col justify-between">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-primary rounded-xl shadow-lg">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 21L12 2L21 21H3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="var(--color-primary)"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-extrabold text-primary drop-shadow-sm">AlumERP</h3>
                  <p className="text-sm opacity-80 font-medium">
                    {currentLanguage === 'en' ? 'Aluminum Extrusion ERP' : 'एल्यूमीनियम एक्सट्रूज़न ERP'}
                  </p>
                </div>
              </div>
              <p className="text-base opacity-90 leading-relaxed mb-6 font-medium">
                {currentLanguage === 'en' ?'Transforming aluminum manufacturing businesses across India with our specialized ERP solution. Reduce manual work, improve quality, and increase profitability.' :'हमारे विशेष ERP समाधान के साथ भारत भर के एल्यूमीनियम निर्माण व्यवसायों को बदलना। मैन्युअल काम कम करें, गुणवत्ता में सुधार करें, और लाभप्रदता बढ़ाएं।'}
              </p>
              <div className="flex space-x-6 mt-4">
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-accent">500+</div>
                  <div className="text-xs opacity-80 font-semibold">
                    {currentLanguage === 'en' ? 'Clients' : 'क्लाइंट्स'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-success">99.2%</div>
                  <div className="text-xs opacity-80 font-semibold">
                    {currentLanguage === 'en' ? 'Success Rate' : 'सफलता दर'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-primary">24/7</div>
                  <div className="text-xs opacity-80 font-semibold">
                    {currentLanguage === 'en' ? 'Support' : 'सहायता'}
                  </div>
                </div>
              </div>
              {/* Social Icons */}
              <div className="flex space-x-4 mt-8">
                <a href="#" className="transition-transform hover:scale-110 hover:drop-shadow-lg" aria-label="LinkedIn"><svg width="28" height="28" fill="currentColor" className="text-primary/80 hover:text-accent" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm15.5 11.268h-3v-5.604c0-1.337-.025-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.601v5.595z"/></svg></a>
                <a href="#" className="transition-transform hover:scale-110 hover:drop-shadow-lg" aria-label="Twitter"><svg width="28" height="28" fill="currentColor" className="text-primary/80 hover:text-accent" viewBox="0 0 24 24"><path d="M24 4.557a9.93 9.93 0 0 1-2.828.775 4.932 4.932 0 0 0 2.165-2.724c-.951.564-2.005.974-3.127 1.195a4.92 4.92 0 0 0-8.384 4.482c-4.086-.205-7.713-2.164-10.141-5.144a4.822 4.822 0 0 0-.666 2.475c0 1.708.87 3.216 2.188 4.099a4.904 4.904 0 0 1-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.936 4.936 0 0 1-2.224.084c.627 1.956 2.444 3.377 4.6 3.417a9.867 9.867 0 0 1-6.102 2.104c-.396 0-.787-.023-1.175-.069a13.945 13.945 0 0 0 7.548 2.212c9.057 0 14.009-7.513 14.009-14.009 0-.213-.005-.425-.014-.636a10.012 10.012 0 0 0 2.457-2.548z"/></svg></a>
                <a href="#" className="transition-transform hover:scale-110 hover:drop-shadow-lg" aria-label="Facebook"><svg width="28" height="28" fill="currentColor" className="text-primary/80 hover:text-accent" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.733 0-1.325.592-1.325 1.326v21.348c0 .733.592 1.326 1.325 1.326h11.495v-9.294h-3.128v-3.622h3.128v-2.672c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.311h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.326v-21.349c0-.734-.593-1.326-1.326-1.326z"/></svg></a>
              </div>
            </div>
            {/* Quick Links */}
            <div className="flex flex-col gap-4">
              <h4 className="font-semibold mb-2 text-primary">{currentLanguage === 'en' ? 'Quick Links' : 'त्वरित लिंक'}</h4>
              <ul className="space-y-2 text-base opacity-90 font-medium">
                <li><a href="#solution" className="hover:text-accent transition-colors">{currentLanguage === 'en' ? 'Solution Overview' : 'समाधान अवलोकन'}</a></li>
                <li><a href="#roi-calculator" className="hover:text-accent transition-colors">{currentLanguage === 'en' ? 'ROI Calculator' : 'ROI कैलकुलेटर'}</a></li>
                <li><a href="#pricing" className="hover:text-accent transition-colors">{currentLanguage === 'en' ? 'Pricing' : 'मूल्य निर्धारण'}</a></li>
                <li><a href="#implementation" className="hover:text-accent transition-colors">{currentLanguage === 'en' ? 'Implementation' : 'कार्यान्वयन'}</a></li>
                <li><a href="#contact-page" className="hover:text-accent transition-colors">{currentLanguage === 'en' ? 'Contact' : 'संपर्क करें'}</a></li>
              </ul>
            </div>
            {/* Newsletter Signup */}
            <div className="col-span-2 md:col-span-1 flex flex-col gap-4 justify-between">
              <h4 className="font-semibold mb-2 text-primary">{currentLanguage === 'en' ? 'Stay Updated' : 'अपडेट रहें'}</h4>
              <form className="flex flex-col sm:flex-row gap-2">
                <input type="email" placeholder={currentLanguage === 'en' ? 'Your email address' : 'आपका ईमेल'} className="rounded-lg px-4 py-2 bg-white/80 border border-border focus:ring-2 focus:ring-accent focus:outline-none text-base text-primary placeholder:text-text-secondary" />
                <button type="submit" className="rounded-lg px-6 py-2 bg-gradient-accent text-white font-semibold shadow-cta hover:scale-105 transition-transform">{currentLanguage === 'en' ? 'Subscribe' : 'सब्सक्राइब करें'}</button>
              </form>
              <p className="text-xs opacity-70 mt-2">{currentLanguage === 'en' ? 'No spam. Only important updates.' : 'कोई स्पैम नहीं। केवल महत्वपूर्ण अपडेट।'}</p>
            </div>
          </div>
        </div>
        {/* Decorative Gradient Blobs */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-gradient-to-br from-accent-200 via-accent-400 to-accent-600 rounded-full opacity-20 blur-3xl -z-10 animate-pulse" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-gradient-to-tr from-primary-200 via-primary-400 to-primary-600 rounded-full opacity-20 blur-3xl -z-10 animate-pulse" />
      </footer>
    </div>
  );
};

export default LandingPage;